import { Manifest, ManifestsByDay, LaneTerminal } from "app/data/static-load/model";
import { Row, Dispatch } from "features/static-load/components/dispatchBoard";

interface GroupedOrigin {
  label: string;
  originCode: string;
  dispatches: Dispatch[];
}

const stripAirportCodeSuffix = (code: string) => code.replace(/\d+$/, "");

export { stripAirportCodeSuffix };

const formatRouteLabel = (
  stops: LaneTerminal[] | undefined,
  destination: LaneTerminal | null | undefined
) => {
  const stopCodes = (stops ?? []).map((stop) => stop.airportCode).filter(Boolean);
  const destinationCode = destination?.airportCode ?? "";
  return [...stopCodes, destinationCode].filter(Boolean).join(" → ");
};

export const transformLanesToRows = (
  lanesByDay: ManifestsByDay | undefined,
  dayOfWeek: number
): Row[] => {
  if (!lanesByDay) return [];

  const lanes = lanesByDay[String(dayOfWeek)] ?? [];

  const groupedByOrigin = lanes.reduce<Record<string, GroupedOrigin>>((acc, lane: Manifest) => {
    const originCode = lane.origin?.airportCode ?? "";
    const originLabel = lane.origin?.terminalName || lane.originName || originCode;
    const destinationCode = lane.destination?.airportCode ?? "";
    const destinationName =
      lane.destination?.terminalName || lane.destinationName || destinationCode;
    const airportLabel = formatRouteLabel(lane.stops, lane.destination) || destinationCode;
    const groupKey = originCode || originLabel;

    const formattedTime = lane.earlyDepartureTime?.slice(0, 5) ?? "";

    const stops = lane.stops?.map((stop) => stop.terminalName || stop.airportCode).filter(Boolean).join(" → ");
    const stopCodes = (lane.stops ?? [])
      .map((stop) => stop.airportCode?.trim())
      .filter((code): code is string => !!code);
    
    const dispatch: Dispatch = {
      id: String(lane.id),
      airport: airportLabel,
      destination: destinationName,
      stops: stops ?? undefined,
      stopCodes,
      destinationCode: destinationCode,
      origin: originLabel,
      time: formattedTime,
      carrier: lane.carrierName || (lane.carrierNumber !== "0" ? lane.carrierNumber ?? undefined : undefined),
      color: lane.carrierNumber === "1325" ? "blue" : "purple",
      drops: lane.dropCount ?? 0,
      carrierCost: String(lane.carrierCost ?? 0),
    };

    if (!acc[groupKey]) {
      acc[groupKey] = { label: originLabel, originCode: groupKey, dispatches: [] };
    }
    acc[groupKey].dispatches.push(dispatch);

    return acc;
  }, {});

  return Object.values(groupedByOrigin).map((group) => ({
    label: group.label,
    originCode: group.originCode,
    dispatches: group.dispatches,
  }));
};
